/**
 * Hala CRM - Admin Users Management Page
 * System Control: Manage CRM users
 * Access: Admin ONLY
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Plus, Search, RefreshCw, Edit2, Trash2, User, Shield,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const ROLE_LABELS = {
  admin: { label: 'مدير النظام', color: COLORS.error },
  supervisor: { label: 'مشرف', color: COLORS.primary },
  consultant: { label: 'مستشار', color: COLORS.gold },
  group_user: { label: 'مستخدم مجموعة', color: COLORS.success },
};

export default function CRMAdminUsersPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Check access
  const hasAccess = user?.role_id === 'admin';

  const fetchUsers = useCallback(async () => {
    if (!user || !hasAccess) return;
    setLoading(true);
    
    try {
      const res = await axios.get(`${API}/crm/users`, { headers: getCRMHeaders() });
      setUsers(res.data?.items || res.data || []);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  }, [user, hasAccess, getCRMHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Access denied for non-admins
  if (!hasAccess) {
    return (
      <div style={{ 
        direction: 'rtl', 
        padding: '60px', 
        textAlign: 'center',
        backgroundColor: COLORS.errorLight,
        borderRadius: '12px',
        margin: '40px',
      }}>
        <AlertCircle size={48} color={COLORS.error} style={{ marginBottom: '16px' }} />
        <h2 style={{ color: COLORS.error, margin: '0 0 8px' }}>غير مصرح بالوصول</h2>
        <p style={{ color: COLORS.textMuted, margin: 0 }}>هذه الصفحة متاحة لمدراء النظام فقط</p>
      </div>
    );
  }

  // Filter users
  const filteredUsers = users.filter(u => 
    !search || 
    u.name_ar?.includes(search) || 
    u.email?.includes(search) ||
    u.username?.includes(search)
  );

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
            إدارة المستخدمين
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
            إدارة مستخدمي نظام CRM ({users.length} مستخدم)
          </p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          <RefreshCw size={16} />
          تحديث
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="بحث بالاسم أو البريد..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px 10px 40px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              textAlign: 'right',
            }}
          />
        </div>
      </div>

      {/* Users Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            جاري التحميل...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Users size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.textMuted} />
            <p style={{ margin: 0, color: COLORS.textMuted }}>لا يوجد مستخدمون</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>المستخدم</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>البريد</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الدور</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const roleConfig = ROLE_LABELS[u.role_id] || { label: u.role_id, color: COLORS.textMuted };
                return (
                  <tr key={u.user_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: COLORS.bgDark,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <User size={20} color={COLORS.textMuted} />
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                            {u.name_ar || u.name || 'بدون اسم'}
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                            @{u.username}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontSize: '13px', color: COLORS.text }}>{u.email || '-'}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        backgroundColor: `${roleConfig.color}15`,
                        color: roleConfig.color,
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        <Shield size={12} />
                        {roleConfig.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {u.status === 'active' ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: COLORS.success, fontSize: '13px' }}>
                          <CheckCircle size={16} />
                          نشط
                        </span>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: COLORS.error, fontSize: '13px' }}>
                          <XCircle size={16} />
                          غير نشط
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Role Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        marginTop: '20px',
        padding: '16px',
        backgroundColor: COLORS.bgDark,
        borderRadius: '8px',
        fontSize: '13px',
        flexWrap: 'wrap',
      }}>
        <span style={{ color: COLORS.textMuted, fontWeight: 500 }}>الأدوار:</span>
        {Object.entries(ROLE_LABELS).map(([key, config]) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: config.color }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: config.color }} />
            {config.label}
          </span>
        ))}
      </div>
    </div>
  );
}
